import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnInit,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { SharedModule } from "../../shared/shared.module";
import {
  SipService,
  ActiveCallInfo,
} from "../../shared/services/sip/sip.service";
import { ToneService } from "../../shared/services/tone/tone.service";
import { Subject, takeUntil } from "rxjs";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import * as FontAwesomeIcons from "@fortawesome/free-solid-svg-icons";
import {
  formatForDisplay,
  toDialable,
  isInternalExtension,
  normalizeToE164,
} from "../utils/phone-number-format";

export interface KeypadDialogData {
  targetNumber: string;
  hasActiveCall: boolean;
  activeCallId: string | null;
  lastDialedNumber: string;
}

export interface KeypadDialogResult {
  targetNumber: string;
  lastDialedNumber: string;
}

@Component({
  selector: "lib-keypad-dialog",
  imports: [SharedModule],
  templateUrl: "./keypad-dialog.component.html",
  styleUrl: "./keypad-dialog.component.scss",
})
export class KeypadDialogComponent implements OnInit, OnDestroy {
  // Icons
  faXmark: IconDefinition = FontAwesomeIcons.faXmark;
  faPhone: IconDefinition = FontAwesomeIcons.faPhone;
  faPhoneSlash: IconDefinition = FontAwesomeIcons.faPhoneSlash;
  faBackspace: IconDefinition = FontAwesomeIcons.faBackspace;
  faTimes: IconDefinition = FontAwesomeIcons.faTimes;
  faRedoAlt: IconDefinition = FontAwesomeIcons.faRedoAlt;

  // State
  targetNumber: string;
  hasActiveCall: boolean;
  activeCallId: string | null;
  lastDialedNumber: string;

  @ViewChild("numberInput") numberInput!: ElementRef<HTMLInputElement>;

  private readonly validDtmfKeys = new Set([
    "0",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "*",
    "#",
  ]);

  readonly keypadKeys: string[][] = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["*", "0", "#"],
  ];

  private destroyed$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<KeypadDialogComponent, KeypadDialogResult>,
    @Inject(MAT_DIALOG_DATA) private data: KeypadDialogData,
    private sipService: SipService,
    private toneService: ToneService,
  ) {
    this.targetNumber = data.targetNumber;
    this.hasActiveCall = data.hasActiveCall;
    this.activeCallId = data.activeCallId;
    this.lastDialedNumber = data.lastDialedNumber;
  }

  ngOnInit(): void {
    this.sipService
      .getActiveCalls()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((calls: ActiveCallInfo[]) => {
        this.hasActiveCall = calls.length > 0;
        this.activeCallId = calls.length > 0 ? calls[0].id : null;
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onKeypadPress(key: string): void {
    if (this.hasActiveCall && this.activeCallId) {
      this.toneService.playDtmfTone(key);
      this.sipService.sendDTMF(this.activeCallId, key).catch((error) => {
        console.error("Error sending DTMF:", error);
      });
    }
    this.targetNumber += key;
  }

  redialLastNumber(): void {
    if (this.lastDialedNumber) {
      this.targetNumber = this.lastDialedNumber;
    }
  }

  onKeypadBackspace(): void {
    if (this.targetNumber.length > 0) {
      this.targetNumber = this.targetNumber.slice(0, -1);
    }
  }

  @HostListener("document:keydown", ["$event"])
  onKeydown(event: KeyboardEvent): void {
    const isInputFocused =
      this.numberInput?.nativeElement === document.activeElement;

    if (this.validDtmfKeys.has(event.key)) {
      if (this.hasActiveCall || !isInputFocused) {
        event.preventDefault();
        this.onKeypadPress(event.key);
      }
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      if (this.hasActiveCall) {
        this.endCall();
      } else if (this.targetNumber) {
        this.startCall();
      }
      return;
    }

    if (event.key === "Backspace" && !this.hasActiveCall) {
      if (!isInputFocused) {
        event.preventDefault();
        this.onKeypadBackspace();
      }
    }
  }

  onTargetNumberBlur(): void {
    if (this.targetNumber && !isInternalExtension(this.targetNumber)) {
      this.targetNumber = formatForDisplay(normalizeToE164(this.targetNumber));
    }
  }

  startCall(): void {
    if (this.targetNumber) {
      this.lastDialedNumber = this.targetNumber;
      this.sipService.makeCall(toDialable(this.targetNumber));
    }
  }

  endCall(): void {
    if (this.activeCallId) {
      this.sipService.endCall(this.activeCallId);
    }
  }

  close(): void {
    this.dialogRef.close({
      targetNumber: this.targetNumber,
      lastDialedNumber: this.lastDialedNumber,
    });
  }
}
